/**
 * web/src/hooks/mutations/useToggleAccess.ts
 * PUT /api/admin/access — toggles one coarse (user, event) grant. Optimistic
 * update on the qk.adminAccess() cache so the grant flips immediately; rolls
 * back on error.
 */
import { toggleAccess, type ToggleAccessBody } from '../../api/access';
import { useOptimisticMatrixMutation } from './_optimistic';
import type { AdminEventGrant } from '../../api/types';

/**
 * Optimistically toggles one (user, event) grant. Rolls back the cache patch
 * on error and surfaces the server message via a toast. Uses the shared
 * admin-matrix optimistic wrapper.
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP PUT, optimistic cache mutation, toast on error
 * @idempotent yes — same body lands the grant at the same final state
 */
export function useToggleAccess() {
  return useOptimisticMatrixMutation<ToggleAccessBody>(
    (body) => toggleAccess(body),
    (prev, body) => {
      const filtered = prev.eventGrants.filter(
        (g) => !(g.userId === body.userId && g.eventId === body.eventId),
      );
      const nextGrants: AdminEventGrant[] = body.granted
        ? [...filtered, { userId: body.userId, eventId: body.eventId }]
        : filtered;
      return { ...prev, eventGrants: nextGrants };
    },
    { errorFallback: 'Failed to update access' },
  );
}
